import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContactSearchComponent from 'components/app/support-desk/tickets-preview/ContactSearchComponent';
import Pagination from 'components/pagination';
import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import EmailNotificationBlackListService from 'services/EmailNotificationBlackList.service';
import PreTicketBlackListService from 'services/PreTicketBlackList.service';
import DialogModal from './DialogModal';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useNotification from 'hooks/useNotification';
import Loading from 'components/loading/Loading';

export const BLACKLIST_TYPE = {
  PRE_TICKET: 'pre-ticket',
  EMAIL: 'email'
};

const parseEmailsFromString = str =>
  (str || '')
    .split(/;\s*/)
    .map(email => email.trim())
    .filter(Boolean);

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getService = type => {
  return type === BLACKLIST_TYPE.PRE_TICKET
    ? PreTicketBlackListService
    : EmailNotificationBlackListService;
};

const BlacklistModal = ({
  show,
  handleClose,
  type = BLACKLIST_TYPE.PRE_TICKET
}) => {
  const service = getService(type);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [filters, setFilters] = useState({
    page: 0,
    size: 20
  });
  const queryParams = new URLSearchParams(filters).toString();
  const [blacklist, setBlacklist] = useState({ content: [] });
  const [itemId, setItemId] = useState('');
  const [showDialogConfirm, setShowDialogConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [showAlertError, setShowAlertError] = useState(false);
  const [error, setError] = useState({});
  const [newEmails, setNewEmails] = useState('');
  const [contactSearchKey, setContactSearchKey] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getData();
  }, [filters]);

  useEffect(() => {
    if (!show) {
      setNewEmails('');
      setContactSearchKey(k => k + 1);
      setShowAlert(false);
      setShowAlertError(false);
      setError({});
    }
  }, [show]);

  const getData = async () => {
    const response = await service.list(queryParams);
    if (response && response.content) {
      setBlacklist(response);
    }
  };

  const deleteItem = async () => {
    const request = await service.delete(itemId);
    if (request?.ok) {
      await getData();
      showSuccessNotification('E-mail removido da blacklist com sucesso');
    } else {
      showErrorNotification(
        request.userMessage || 'Não foi possível remover o e-mail.'
      );
    }
    setShowDialogConfirm(false);
  };

  const handleDelete = useCallback(id => {
    setItemId(id);
    setShowDialogConfirm(true);
  }, []);

  const columns = [
    {
      accessorKey: 'sender',
      header: 'E-mail',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row }) => row.original.sender
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: {
          className: 'text-900 text-nowrap',
          style: { width: '1%' }
        }
      },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="d-flex justify-content-center">
            <Button
              variant="link"
              size="sm"
              onClick={() => handleDelete(item.id)}
              aria-label="Remover da blacklist"
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </Button>
          </div>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: blacklist.content,
    columns
  });

  const handleAddToBlacklist = async e => {
    e?.preventDefault();
    const parsed = parseEmailsFromString(newEmails);
    const emails = parsed.filter(isValidEmail);
    if (emails.length === 0) {
      if (parsed.length > 0) {
        setError({
          description:
            'Nenhum e-mail válido encontrado. Verifique o formato (ex: email@dominio.com) e separe por ponto e vírgula (;) para vários e-mails.'
        });
        setShowAlertError(true);
      }
      return;
    }
    setLoading(true);
    setIsAdding(true);
    setShowAlertError(false);
    try {
      const results = await Promise.all(
        emails.map(email => service.add({ sender: [email] }))
      );
      const allOk = results.every(r => Array.isArray(r));
      if (allOk) {
        setNewEmails('');
        setContactSearchKey(k => k + 1);
        await getData();
        showSuccessNotification(
          'E-mail(s) adicionado(s) à blacklist com sucesso'
        );
      } else {
        const failed = results.find(r => !Array.isArray(r));
        showErrorNotification(
          failed?.userMessage || 'Não foi possível adicionar o(s) e-mail(s).'
        );
      }
    } catch (err) {
      showErrorNotification(err?.message || 'Erro ao adicionar e-mail(s).');
    } finally {
      setIsAdding(false);
      setLoading(false);
    }
  };

  const tableId = `blacklist-${type.replace('-', '')}`;

  return (
    <>
      {loading && <Loading message="Adicionando à blacklist..." />}
      <DialogModal
        message="Tem certeza que deseja desbloquear este remetente?"
        showModal={showDialogConfirm}
        handleClose={() => setShowDialogConfirm(false)}
        response={deleteItem}
      />
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Emails bloqueados</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <Form onSubmit={handleAddToBlacklist} className="mb-3">
            <div className="blacklist-input-append position-relative mb-2">
              <ContactSearchComponent
                key={contactSearchKey}
                label="E-mails para adicionar à blacklist"
                showLabel={false}
                showTooltip={false}
                customerId={null}
                value={newEmails}
                onChange={setNewEmails}
              />
              <Button
                type="submit"
                variant="primary"
                disabled={
                  !parseEmailsFromString(newEmails).filter(isValidEmail)
                    .length || isAdding
                }
                aria-label="Salvar"
                className="blacklist-input-append-btn position-absolute px-3"
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
            <style>{`
              .blacklist-input-append { padding-right: 2.75rem; }
              .blacklist-input-append > div { margin-top: 0; }
              .blacklist-input-append .form-control {
                padding-right: 0.5rem;
                border-right: none;
                border-radius: 50rem 0 0 50rem;
                max-width: 100%;
              }
              .blacklist-input-append .form-control:focus {
                box-shadow: none;
                border-right: none;
                outline: none;
              }
              .blacklist-input-append-btn {
                top: 0;
                right: 0;
                width: 2.75rem;
                height: calc(1.5em + 0.5rem + 2px);
                border-left: none;
                border-radius: 0 50rem 50rem 0;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
              }
            `}</style>
          </Form>
          <div id={tableId}>
            {blacklist.content.length === 0 ? (
              <p className="text-muted text-center mb-0 py-4">
                Nenhum registro encontrado...
              </p>
            ) : (
              <>
                <AdvanceTableProvider {...table}>
                  <AdvanceTable
                    table
                    headerClassName="text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </AdvanceTableProvider>
                <Pagination
                  data={blacklist}
                  filters={filters}
                  setFilters={setFilters}
                />
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BlacklistModal;
